import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import AuthContext from '../../components/shared/AuthContext';

const LEARNING_NEXT_TIMEOUT_MS = 12_000;

export function useLearningNext(options = {}) {
  const { enabled = true } = options;
  const { apiClient } = useContext(AuthContext);
  const [next, setNext] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState('');
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    if (!enabled) return;
    const id = ++requestRef.current;
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.get('/api/v1/learning/next', {
        timeout: LEARNING_NEXT_TIMEOUT_MS,
      });
      if (id === requestRef.current) setNext(res.data);
    } catch (err) {
      if (id !== requestRef.current) return;
      if (axios.isCancel(err)) return;
      const timedOut = err?.code === 'ECONNABORTED';
      setError(
        timedOut
          ? 'Personalized suggestion timed out — showing a quick recommendation below.'
          : 'Could not load your next learning action — showing a quick recommendation below.'
      );
      setNext(null);
    } finally {
      if (id === requestRef.current) setLoading(false);
    }
  }, [apiClient, enabled]);

  useEffect(() => {
    load();
  }, [load]);

  return { next, loading, error, reload: load };
}

export function useTodayPlan(minutes) {
  const { apiClient } = useContext(AuthContext);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const debounceRef = useRef(null);
  const requestRef = useRef(0);

  const load = useCallback(async (budget) => {
    const id = ++requestRef.current;
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.get('/api/v1/learning/today', {
        params: { minutes: budget },
      });
      if (id === requestRef.current) setPlan(res.data);
    } catch {
      if (id === requestRef.current) {
        setPlan(null);
        setError('Could not build today’s plan. Try again.');
      }
    } finally {
      if (id === requestRef.current) setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(minutes), 280);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [minutes, load]);

  const reload = useCallback(() => load(minutes), [load, minutes]);

  return { plan, loading, error, reload };
}

export function useMasteryMap() {
  const { apiClient } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (sync = true) => {
    setLoading(true);
    setError('');
    try {
      if (sync) {
        setSyncing(true);
        await apiClient.post('/api/v1/learning/mastery/sync').catch(() => {});
        setSyncing(false);
      }
      const res = await apiClient.get('/api/v1/learning/mastery', { params: { size: 120 } });
      setData(res.data);
    } catch {
      setError('Could not load mastery map.');
    } finally {
      setLoading(false);
      setSyncing(false);
    }
  }, [apiClient]);

  useEffect(() => {
    load(true);
  }, [load]);

  return { data, loading, syncing, error, reload: () => load(true) };
}
